import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader, TrendingUp, Sparkles, RefreshCw, AlertTriangle } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  fetchAdAccounts, fetchAnalyticsOverview, fetchAnalyticsTimeseries, fetchAnalyticsCampaigns,
  fetchAnalyticsAdSets, fetchAnalyticsAds, fetchAnalyticsBreakdown, fetchAnalyticsSuggestions, setSelectedAdAccount,
} from '../../redux/metaAdsSlice';
import { formatCurrency, getCurrencySymbol } from '../../utils/currency';
import { downloadMetaAdsReportPdf } from '../../utils/metaAdsReportPdf';
import { FileDown } from 'lucide-react';
import { PageLoader } from '../../components/crm/ui';

const RANGE_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
];

const BREAKDOWN_OPTIONS = [
  { value: 'age', label: 'Age' },
  { value: 'gender', label: 'Gender' },
  { value: 'placement', label: 'Placement' },
  { value: 'region', label: 'Region' },
  { value: 'device', label: 'Device' },
  { value: 'hour', label: 'Time of Day' },
];

function KpiCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5 border-l-4" style={{ borderLeftColor: accent }}>
      <p className="text-[11px] font-semibold text-[#706E6B] mb-1">{label}</p>
      <h3 className="text-2xl font-bold text-[#2E2E2E] tabular-nums">{value}</h3>
    </div>
  );
}

export default function MetaAdsAnalyticsPage() {
  const dispatch = useDispatch();
  const {
    connected, adAccounts, analyticsOverview, analyticsTimeseries, analyticsCampaigns, analyticsAdSets, analyticsAds, analyticsBreakdown, analyticsLoading,
    selectedAdAccountId, suggestions, suggestionsLoading,
  } = useSelector((s) => s.metaAds);
  const [days, setDays] = useState(30);
  const [breakdownDimension, setBreakdownDimension] = useState('age');
  // Local-only override for "viewing" a different account without persisting
  // it yet — defaults to the persisted selection once it loads, so every page
  // that reads selectedAdAccountId shows the same default on first load.
  const [adAccountId, setAdAccountId] = useState('');
  // '' = whole account (previous behavior); a Meta platform campaign id
  // scopes the KPI cards and trend chart to just that one campaign, using
  // the same campaigns already listed in the breakdown table below.
  const [campaignFilter, setCampaignFilter] = useState('');
  // '' = no ad-set-level bifurcation; a Meta ad set id further scopes the KPI
  // cards and trend chart down to just that one ad set within the selected
  // (or "all") campaign, using the ad sets already listed in the breakdown
  // table below.
  const [adSetFilter, setAdSetFilter] = useState('');

  useEffect(() => { if (connected) dispatch(fetchAdAccounts()); }, [connected, dispatch]);

  useEffect(() => {
    if (selectedAdAccountId && !adAccountId) setAdAccountId(selectedAdAccountId);
  }, [selectedAdAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveAdAccountId = adAccountId || selectedAdAccountId || '';

  const chooseAdAccount = (id) => {
    setAdAccountId(id);
    if (id) dispatch(setSelectedAdAccount(id));
  };

  useEffect(() => {
    if (!connected) return;
    const params = { days, ...(effectiveAdAccountId ? { adAccountId: effectiveAdAccountId } : {}) };
    // The breakdown tables always show every campaign/ad set regardless of
    // the filter — only the KPI cards/chart narrow down to one.
    dispatch(fetchAnalyticsCampaigns(params));
    // Ad-set breakdown is scoped by the selected campaign (if any) — Meta's
    // adset-level insights don't need a campaign filter to work account-wide,
    // but scoping it keeps the bifurcated table meaningful once a campaign
    // is picked, rather than showing every ad set across every campaign.
    dispatch(fetchAnalyticsAdSets(campaignFilter ? { ...params, campaignId: campaignFilter } : params));
    // Ad-level breakdown is the finest-grained view — scoped by whichever of
    // campaign/ad set is selected, same reasoning as the ad-set breakdown
    // above (keeps the table meaningful rather than dumping every ad in the
    // whole account).
    dispatch(fetchAnalyticsAds({
      ...params,
      ...(campaignFilter ? { campaignId: campaignFilter } : {}),
      ...(adSetFilter ? { adSetId: adSetFilter } : {}),
    }));
    const scopedParams = {
      ...params,
      ...(campaignFilter ? { campaignId: campaignFilter } : {}),
      ...(adSetFilter ? { adSetId: adSetFilter } : {}),
    };
    dispatch(fetchAnalyticsOverview(scopedParams));
    dispatch(fetchAnalyticsTimeseries(scopedParams));
  }, [connected, days, effectiveAdAccountId, campaignFilter, adSetFilter, dispatch]);

  useEffect(() => {
    if (!connected) return;
    dispatch(fetchAnalyticsBreakdown({
      days,
      ...(effectiveAdAccountId ? { adAccountId: effectiveAdAccountId } : {}),
      ...(campaignFilter ? { campaignId: campaignFilter } : {}),
      ...(adSetFilter ? { adSetId: adSetFilter } : {}),
      dimension: breakdownDimension,
    }));
  }, [connected, days, effectiveAdAccountId, campaignFilter, adSetFilter, breakdownDimension, dispatch]);

  // Switching ad accounts invalidates whatever campaign/ad set was selected
  // for the previous one.
  useEffect(() => { setCampaignFilter(''); setAdSetFilter(''); }, [effectiveAdAccountId]);
  // Switching campaigns invalidates an ad-set selection scoped to the old one.
  useEffect(() => { setAdSetFilter(''); }, [campaignFilter]);

  useEffect(() => {
    if (!connected) return;
    dispatch(fetchAnalyticsSuggestions({ adAccountId: effectiveAdAccountId }));
  }, [connected, effectiveAdAccountId, dispatch]);

  if (!connected) {
    return (
      <div className="text-center py-16 border border-dashed border-[#D8DDE6] rounded-[4px]">
        <TrendingUp size={28} className="mx-auto text-[#D8DDE6] mb-2" />
        <p className="text-[13px] text-[#5C5C5C]">Connect a Meta Ads account in Settings to see analytics.</p>
      </div>
    );
  }

  const o = analyticsOverview || {};
  // Overview/timeseries/campaign-breakdown are all scoped to the same
  // resolved ad account per this page's fetch params, so one currency
  // lookup covers every number shown here — this account, for example, is
  // INR, not the hardcoded "$" this page used to show everywhere.
  const currency = adAccounts.find((a) => a.id === o.adAccountId)?.currency || 'USD';
  const currencySymbol = getCurrencySymbol(currency);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        {/* Filter selects are grouped together with a fixed gap regardless of
            how many of the three are actually present — previously this
            whole row (including the trailing range/download group) shared a
            single `justify-between`, which spread apart however many
            children happened to be rendered (0-3 conditional selects plus
            the action group) instead of keeping filters visually grouped on
            the left with actions pinned to the right. */}
        <div className="flex flex-wrap items-center gap-3">
          {adAccounts.length > 1 && (
            <select
              className="border border-[#D8DDE6] rounded-[4px] px-3 py-1.5 text-[13px]"
              value={effectiveAdAccountId}
              onChange={(e) => chooseAdAccount(e.target.value)}
            >
              <option value="">Default account</option>
              {adAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          {analyticsCampaigns.length > 0 && (
            <select
              className="border border-[#D8DDE6] rounded-[4px] px-3 py-1.5 text-[13px]"
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
            >
              <option value="">All campaigns</option>
              {analyticsCampaigns.map((c) => <option key={c.campaignId} value={c.campaignId}>{c.campaignName}</option>)}
            </select>
          )}
          {analyticsAdSets.length > 0 && (
            <select
              className="border border-[#D8DDE6] rounded-[4px] px-3 py-1.5 text-[13px]"
              value={adSetFilter}
              onChange={(e) => setAdSetFilter(e.target.value)}
            >
              <option value="">All ad sets</option>
              {analyticsAdSets.map((a) => <option key={a.adSetId} value={a.adSetId}>{a.adSetName}</option>)}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDays(opt.value)}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                days === opt.value ? 'bg-[#0176D3] text-white' : 'bg-white border border-[#D8DDE6] text-[#5C5C5C] hover:bg-[#F4F6F9]'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <button
            onClick={() => downloadMetaAdsReportPdf({ adAccountId: effectiveAdAccountId || undefined, days })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-[#D8DDE6] text-[#5C5C5C] hover:bg-[#F4F6F9] transition-colors whitespace-nowrap"
          >
            <FileDown size={14} /> Download Deep Report
          </button>
        </div>
      </div>

      {analyticsLoading && !analyticsOverview ? (
        <PageLoader inline />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
            <KpiCard label="Spend" value={formatCurrency(o.spend || 0, currency)} accent="#0176D3" />
            <KpiCard label="Impressions" value={Number(o.impressions || 0).toLocaleString()} accent="#706E6B" />
            <KpiCard label="Clicks" value={Number(o.clicks || 0).toLocaleString()} accent="#8E44EC" />
            <KpiCard label="CTR" value={`${Number(o.ctr || 0).toFixed(2)}%`} accent="#FF9500" />
            <KpiCard label="Leads (Meta)" value={Number(o.conversions || 0).toLocaleString()} accent="#2E844A" />
            <KpiCard label="CRM Leads" value={Number(o.crmLeads || 0).toLocaleString()} accent="#0EA5A5" />
            <KpiCard label="Cost / Lead" value={o.conversions ? formatCurrency(o.cpl || 0, currency) : '—'} accent="#BA0517" />
          </div>
          <p className="text-[11px] text-[#5C5C5C] -mt-2">
            "Leads (Meta)" is Meta's own self-reported count for this period; "CRM Leads" is what actually landed in this CRM
            (via the leadgen webhook) — the two can differ due to attribution delay or leads Meta doesn't tag as its "lead" action.
          </p>

          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-6">
            <h2 className="text-[14px] font-bold text-[#2E2E2E] mb-4">
              Spend &amp; Leads over time
              {(campaignFilter || adSetFilter) && (
                <span className="ml-2 text-[12px] font-normal text-[#5C5C5C]">
                  — {adSetFilter
                    ? (analyticsAdSets.find((a) => a.adSetId === adSetFilter)?.adSetName || 'selected ad set')
                    : (analyticsCampaigns.find((c) => c.campaignId === campaignFilter)?.campaignName || 'selected campaign')}
                </span>
              )}
            </h2>
            {analyticsTimeseries.length === 0 ? (
              <div className="flex items-center justify-center h-[260px] text-[13px] text-[#5C5C5C]">No data for this period yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={analyticsTimeseries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="spend" name={`Spend (${currencySymbol})`} stroke="#0176D3" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="conversions" name="Leads (Meta)" stroke="#2E844A" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="crmLeads" name="CRM Leads" stroke="#0EA5A5" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[14px] font-bold text-[#2E2E2E] flex items-center gap-2">
                <Sparkles size={15} className="text-[#8E44EC]" /> AI Suggestions
              </h2>
              <button
                onClick={() => dispatch(fetchAnalyticsSuggestions({ adAccountId: effectiveAdAccountId, force: true }))}
                disabled={suggestionsLoading}
                className="flex items-center gap-1.5 text-[12px] font-semibold text-[#0176D3] hover:underline disabled:opacity-60"
              >
                <RefreshCw size={13} className={suggestionsLoading ? 'animate-spin' : ''} /> Regenerate
              </button>
            </div>
            {suggestionsLoading && !suggestions ? (
              <div className="flex items-center justify-center h-24 text-[13px] text-[#5C5C5C]">
                <Loader className="animate-spin mr-2" size={16} /> Analyzing your account's performance…
              </div>
            ) : !suggestions ? (
              <p className="text-[13px] text-[#5C5C5C]">No suggestions yet.</p>
            ) : (
              <div className="grid sm:grid-cols-3 gap-5">
                <div>
                  <p className="text-[11px] font-semibold text-[#706E6B] mb-2">Insights</p>
                  <ul className="space-y-1.5">
                    {(suggestions.insights || []).map((t, i) => (
                      <li key={i} className="text-[13px] text-[#2E2E2E]">{t}</li>
                    ))}
                    {(suggestions.insights || []).length === 0 && <li className="text-[13px] text-[#5C5C5C]">—</li>}
                  </ul>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-[#706E6B] mb-2">Suggestions</p>
                  <ul className="space-y-2.5">
                    {(suggestions.suggestions || []).map((s, i) => (
                      <li key={i}>
                        <p className="text-[13px] font-semibold text-[#2E2E2E]">{s.title}</p>
                        <p className="text-[12px] text-[#5C5C5C]">{s.rationale}</p>
                        <p className="text-[12px] text-[#0176D3] font-medium">→ {s.suggestedAction}</p>
                      </li>
                    ))}
                    {(suggestions.suggestions || []).length === 0 && <li className="text-[13px] text-[#5C5C5C]">—</li>}
                  </ul>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-[#706E6B] mb-2">Warnings</p>
                  <ul className="space-y-1.5">
                    {(suggestions.warnings || []).map((t, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[13px] text-[#BA0517]">
                        <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" /> {t}
                      </li>
                    ))}
                    {(suggestions.warnings || []).length === 0 && <li className="text-[13px] text-[#5C5C5C]">—</li>}
                  </ul>
                </div>
              </div>
            )}
            {suggestions?.generatedAt && (
              <p className="text-[11px] text-[#5C5C5C] mt-4">
                {suggestions.cached ? 'Cached' : 'Generated'} {new Date(suggestions.generatedAt).toLocaleString()}
              </p>
            )}
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#D8DDE6]">
              <h2 className="text-[14px] font-bold text-[#2E2E2E]">Campaign breakdown</h2>
            </div>
            {analyticsCampaigns.length === 0 ? (
              <div className="text-center py-10 text-[13px] text-[#5C5C5C]">No campaign activity for this period.</div>
            ) : (
              <div className="slds2-table-wrap"><table className="w-full text-[13px]">
                <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2.5">Campaign</th>
                    <th className="text-right px-4 py-2.5">Impressions</th>
                    <th className="text-right px-4 py-2.5">Clicks</th>
                    <th className="text-right px-4 py-2.5">CTR</th>
                    <th className="text-right px-4 py-2.5">Spend</th>
                    <th className="text-right px-4 py-2.5">Leads (Meta)</th>
                    <th className="text-right px-4 py-2.5" title="Leads actually captured in this CRM for this campaign, via the leadgen webhook — may differ from Meta's self-reported count above.">CRM Leads</th>
                    <th className="text-right px-4 py-2.5">CPL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D8DDE6]">
                  {analyticsCampaigns.map((c) => (
                    <tr key={c.campaignId} className={c.campaignId === campaignFilter ? 'bg-[#EAF4FE]' : undefined}>
                      <td className="px-4 py-2.5 font-semibold text-[#2E2E2E]">
                        <button
                          type="button"
                          onClick={() => setCampaignFilter(c.campaignId === campaignFilter ? '' : c.campaignId)}
                          className="hover:underline text-left"
                        >
                          {c.campaignName}
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-right">{c.impressions.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">{c.clicks.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">{Number(c.ctr || 0).toFixed(2)}%</td>
                      <td className="px-4 py-2.5 text-right">{formatCurrency(c.spend, currency)}</td>
                      <td className="px-4 py-2.5 text-right">{c.conversions}</td>
                      <td className="px-4 py-2.5 text-right">{c.crmLeads}</td>
                      <td className="px-4 py-2.5 text-right">{c.conversions ? formatCurrency(c.cpl, currency) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#D8DDE6]">
              <h2 className="text-[14px] font-bold text-[#2E2E2E]">
                Ad set breakdown
                {campaignFilter && (
                  <span className="ml-2 text-[12px] font-normal text-[#5C5C5C]">
                    — {analyticsCampaigns.find((c) => c.campaignId === campaignFilter)?.campaignName || 'selected campaign'}
                  </span>
                )}
              </h2>
            </div>
            {analyticsAdSets.length === 0 ? (
              <div className="text-center py-10 text-[13px] text-[#5C5C5C]">No ad set activity for this period.</div>
            ) : (
              <div className="slds2-table-wrap"><table className="w-full text-[13px]">
                <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2.5">Ad Set</th>
                    <th className="text-left px-4 py-2.5">Campaign</th>
                    <th className="text-right px-4 py-2.5">Impressions</th>
                    <th className="text-right px-4 py-2.5">Clicks</th>
                    <th className="text-right px-4 py-2.5">CTR</th>
                    <th className="text-right px-4 py-2.5">Spend</th>
                    <th className="text-right px-4 py-2.5">Leads (Meta)</th>
                    <th className="text-right px-4 py-2.5" title="Leads actually captured in this CRM for this ad set, via the leadgen webhook — may differ from Meta's self-reported count above.">CRM Leads</th>
                    <th className="text-right px-4 py-2.5">CPL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D8DDE6]">
                  {analyticsAdSets.map((a) => (
                    <tr key={a.adSetId} className={a.adSetId === adSetFilter ? 'bg-[#EAF4FE]' : undefined}>
                      <td className="px-4 py-2.5 font-semibold text-[#2E2E2E]">
                        <button
                          type="button"
                          onClick={() => setAdSetFilter(a.adSetId === adSetFilter ? '' : a.adSetId)}
                          className="hover:underline text-left"
                        >
                          {a.adSetName}
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-[#5C5C5C]">{a.campaignName}</td>
                      <td className="px-4 py-2.5 text-right">{a.impressions.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">{a.clicks.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">{Number(a.ctr || 0).toFixed(2)}%</td>
                      <td className="px-4 py-2.5 text-right">{formatCurrency(a.spend, currency)}</td>
                      <td className="px-4 py-2.5 text-right">{a.conversions}</td>
                      <td className="px-4 py-2.5 text-right">{a.crmLeads}</td>
                      <td className="px-4 py-2.5 text-right">{a.conversions ? formatCurrency(a.cpl, currency) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#D8DDE6]">
              <h2 className="text-[14px] font-bold text-[#2E2E2E]">
                Ad breakdown
                {adSetFilter && (
                  <span className="ml-2 text-[12px] font-normal text-[#5C5C5C]">
                    — {analyticsAdSets.find((a) => a.adSetId === adSetFilter)?.adSetName || 'selected ad set'}
                  </span>
                )}
              </h2>
            </div>
            {analyticsAds.length === 0 ? (
              <div className="text-center py-10 text-[13px] text-[#5C5C5C]">No ad activity for this period.</div>
            ) : (
              <div className="slds2-table-wrap"><table className="w-full text-[13px]">
                <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2.5">Ad</th>
                    <th className="text-left px-4 py-2.5">Ad Set</th>
                    <th className="text-right px-4 py-2.5">Impressions</th>
                    <th className="text-right px-4 py-2.5">Clicks</th>
                    <th className="text-right px-4 py-2.5">CTR</th>
                    <th className="text-right px-4 py-2.5">Spend</th>
                    <th className="text-right px-4 py-2.5">Leads (Meta)</th>
                    <th className="text-right px-4 py-2.5" title="Leads actually captured in this CRM for this ad, via the leadgen webhook — may differ from Meta's self-reported count above.">CRM Leads</th>
                    <th className="text-right px-4 py-2.5">CPL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D8DDE6]">
                  {analyticsAds.map((a) => (
                    <tr key={a.adId}>
                      <td className="px-4 py-2.5 font-semibold text-[#2E2E2E]">{a.adName}</td>
                      <td className="px-4 py-2.5 text-[#5C5C5C]">{a.adSetName}</td>
                      <td className="px-4 py-2.5 text-right">{a.impressions.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">{a.clicks.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">{Number(a.ctr || 0).toFixed(2)}%</td>
                      <td className="px-4 py-2.5 text-right">{formatCurrency(a.spend, currency)}</td>
                      <td className="px-4 py-2.5 text-right">{a.conversions}</td>
                      <td className="px-4 py-2.5 text-right">{a.crmLeads}</td>
                      <td className="px-4 py-2.5 text-right">{a.conversions ? formatCurrency(a.cpl, currency) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#D8DDE6] flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-[14px] font-bold text-[#2E2E2E]">Breakdown</h2>
              <div className="flex gap-1">
                {BREAKDOWN_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setBreakdownDimension(opt.value)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border  whitespace-nowrap ${breakdownDimension === opt.value ? 'bg-[#0176D3] border-[#0176D3] text-white' : 'border-[#D8DDE6] text-[#2E2E2E]'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {analyticsBreakdown.length === 0 ? (
              <div className="text-center py-10 text-[13px] text-[#5C5C5C]">No activity to break down for this period.</div>
            ) : (
              <div className="slds2-table-wrap"><table className="w-full text-[13px]">
                <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2.5">{BREAKDOWN_OPTIONS.find((o) => o.value === breakdownDimension)?.label}</th>
                    <th className="text-right px-4 py-2.5">Impressions</th>
                    <th className="text-right px-4 py-2.5">Clicks</th>
                    <th className="text-right px-4 py-2.5">CTR</th>
                    <th className="text-right px-4 py-2.5">Spend</th>
                    <th className="text-right px-4 py-2.5">Leads</th>
                    <th className="text-right px-4 py-2.5">CPL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D8DDE6]">
                  {analyticsBreakdown.map((row, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 font-semibold text-[#2E2E2E]">{row.label}</td>
                      <td className="px-4 py-2.5 text-right">{row.impressions.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">{row.clicks.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">{Number(row.ctr || 0).toFixed(2)}%</td>
                      <td className="px-4 py-2.5 text-right">{formatCurrency(row.spend, currency)}</td>
                      <td className="px-4 py-2.5 text-right">{row.conversions}</td>
                      <td className="px-4 py-2.5 text-right">{row.conversions ? formatCurrency(row.cpl, currency) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
